import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Plus, Trash2, X, Send, Archive, Download,
  CheckCircle2, Clock, AlertCircle, Users, Building,
  MailCheck, MessageSquare, Eye, Ban, FileCheck, Search, ShieldCheck, Settings, Key
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import DocumentWizard from '@/components/DocumentWizard';
import BRAND from '@/brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_META = {
  draft:     { label: 'Kladde',        color: 'bg-slate-500',  icon: FileText },
  pending:   { label: 'Afventer',      color: 'bg-amber-500',  icon: Clock },
  signed:    { label: 'Underskrevet',  color: 'bg-green-600',  icon: CheckCircle2 },
  archived:  { label: 'Arkiveret',     color: 'bg-brand-600',   icon: Archive },
  cancelled: { label: 'Annulleret',    color: 'bg-red-600',    icon: Ban },
};

const SIGNER_STATUS = {
  pending:   { label: 'Ikke sendt',    color: 'text-th-text-muted' },
  opened:    { label: 'Åbnet',         color: 'text-amber-500' },
  read:      { label: 'Gennemlæst',    color: 'text-amber-500' },
  verifying: { label: 'Verificerer',   color: 'text-brand-500' },
  signed:    { label: 'Underskrevet',  color: 'text-green-500' },
  declined:  { label: 'Afvist',        color: 'text-red-500' },
};

function fmtBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('da-DK', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

// ─── Tilføj underskriver ──────────────────────────────────────────────────────

function AddSignerDialog({ documentId, onClose, onAdded }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [cvr, setCvr] = useState('');
  const [company, setCompany] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [saving, setSaving] = useState(false);

  const lookupCvr = async () => {
    const clean = cvr.replace(/\D/g, '');
    if (clean.length !== 8) { toast.error('CVR skal være 8 cifre'); return; }
    setLookingUp(true);
    try {
      const r = await axios.get(`${API}/documents/lookup-cvr`, { params: { cvr: clean } });
      if (r.data?.name) {
        setCompany(r.data);
        toast.success(`Fundet: ${r.data.name}`);
      } else {
        setCompany(null);
        toast.error('Selskab ikke fundet');
      }
    } catch {
      toast.error('Kunne ikke slå CVR op');
    } finally {
      setLookingUp(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Udfyld navn'); return; }
    if (!email.includes('@')) { toast.error('Ugyldig email'); return; }
    if (phone.replace(/\D/g, '').length < 8) { toast.error('Ugyldigt mobilnummer'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/documents/${documentId}/signers`, {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
        cvr: cvr.replace(/\D/g, '') || null,
      });
      toast.success('Underskriver tilføjet');
      onAdded();
      onClose();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Kunne ikke tilføje underskriver');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-th-card border border-th-border rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Users size={18} className="text-brand-500" />
            <h2 className="text-lg font-semibold text-th-text">Tilføj underskriver</h2>
          </div>
          <button onClick={onClose} className="text-th-text-muted hover:text-th-text transition-colors"><X size={20} /></button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-th-text-secondary mb-1">Navn <span className="text-red-400">*</span></label>
            <Input autoFocus value={name} onChange={e => setName(e.target.value)}
              placeholder="Fulde navn" className="bg-th-input border-th-border-light text-th-text" />
          </div>

          <div>
            <label className="block text-sm font-medium text-th-text-secondary mb-1">E-mail <span className="text-red-400">*</span></label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="navn@firma.dk" className="bg-th-input border-th-border-light text-th-text" />
          </div>

          <div>
            <label className="block text-sm font-medium text-th-text-secondary mb-1">Mobilnummer <span className="text-red-400">*</span></label>
            <Input value={phone} onChange={e => setPhone(e.target.value)}
              placeholder="20 31 20 00" className="bg-th-input border-th-border-light text-th-text" />
            <p className="text-xs text-th-text-muted mt-1">
              Underskriveren skal indtaste præcis dette nummer for at kunne underskrive.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-th-text-secondary mb-1">CVR (valgfrit)</label>
            <div className="flex gap-2">
              <Input value={cvr} onChange={e => { setCvr(e.target.value); setCompany(null); }}
                placeholder="12345678" maxLength={8}
                className="bg-th-input border-th-border-light text-th-text" />
              <button type="button" onClick={lookupCvr} disabled={lookingUp}
                className="shrink-0 px-3 rounded-lg border border-th-border text-th-text-secondary hover:bg-th-hover transition-colors disabled:opacity-50">
                {lookingUp ? '...' : <Search size={16} />}
              </button>
            </div>
            {company && (
              <div className="mt-2 bg-th-hover border border-th-border rounded-lg px-3 py-2">
                <p className="text-sm text-th-text font-medium">{company.name}</p>
                {company.address && <p className="text-xs text-th-text-muted">{company.address}</p>}
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 rounded-lg text-sm font-medium border border-th-border text-th-text-secondary hover:bg-th-hover transition-colors">
              Annuller
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-btn hover:bg-btn-hover-strong text-btn-fg transition-colors disabled:opacity-50">
              {saving ? 'Gemmer...' : 'Tilføj'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

// ─── Attestation-badge ────────────────────────────────────────────────────────

function AttestRow({ icon: Icon, label, at, sessionId, extra }) {
  const ok = !!at;
  return (
    <div className={`flex items-start gap-2.5 px-3 py-2 rounded-lg ${ok ? 'bg-green-500/5 border border-green-500/20' : 'bg-th-hover border border-th-border'}`}>
      <Icon size={15} className={`mt-0.5 shrink-0 ${ok ? 'text-green-500' : 'text-th-text-faint'}`} />
      <div className="min-w-0 flex-1">
        <p className={`text-xs font-medium ${ok ? 'text-green-500' : 'text-th-text-muted'}`}>
          {label}{ok ? '' : ' — afventer'}
        </p>
        {ok && (
          <>
            <p className="text-xs text-th-text-muted mt-0.5">{fmtDate(at)}</p>
            {sessionId && (
              <p className="text-[11px] text-th-text-faint font-mono truncate" title={sessionId}>
                Session: {sessionId}
              </p>
            )}
            {extra && <p className="text-[11px] text-th-text-faint">{extra}</p>}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Hovedvisning ─────────────────────────────────────────────────────────────

function DocumentsView() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [selected, setSelected] = useState(null);     // dokument-id
  // API'et returnerer dokumentet fladt med en nested signers-array.
  const [detail, setDetail] = useState(null);         // {…document, signers:[…]}
  const [detailLoading, setDetailLoading] = useState(false);
  const [showAddSigner, setShowAddSigner] = useState(false);
  const [busy, setBusy] = useState(false);

  const fetchDocs = () => {
    setLoading(true);
    axios.get(`${API}/documents`)
      .then(r => setDocs(r.data || []))
      .catch(() => toast.error('Kunne ikke hente dokumenter'))
      .finally(() => setLoading(false));
  };

  const fetchDetail = (id) => {
    setDetailLoading(true);
    axios.get(`${API}/documents/${id}`)
      .then(r => setDetail(r.data))
      .catch(() => toast.error('Kunne ikke hente dokument'))
      .finally(() => setDetailLoading(false));
  };

  useEffect(() => { fetchDocs(); }, []);
  useEffect(() => { if (selected) fetchDetail(selected); else setDetail(null); }, [selected]);

  // Levende opdatering af det aabne dokument.
  //
  // EventSource kan ikke saette en Authorization-header, og at laegge et token
  // i adressen ville skrive det i nginx' log og i browserens historik. Derfor
  // laeses stroemmen med fetch, som beholder headeren — formatet er det samme
  // text/event-stream, vi bare selv deler op ved de blanke linjer.
  //
  // Underskrifterne falder, mens afsenderen sidder og kigger. Uden det her
  // skulle han selv taste F5 for at se dem.
  useEffect(() => {
    if (!selected) return;
    const afbryd = new AbortController();
    let levende = true;

    const lyt = async () => {
      while (levende) {
        try {
          const svar = await fetch(`${API}/documents/${selected}/stream`, {
            headers: { Authorization: axios.defaults.headers.common['Authorization'] || '' },
            signal: afbryd.signal,
          });
          if (!svar.ok || !svar.body) throw new Error(`HTTP ${svar.status}`);

          const laeser = svar.body.getReader();
          const afkod = new TextDecoder();
          let rest = '';
          while (levende) {
            const { value, done } = await laeser.read();
            if (done) break;
            rest += afkod.decode(value, { stream: true });
            // Hændelser er adskilt af en blank linje. Det sidste stykke kan
            // vaere halvt, saa det bliver liggende til naeste gang.
            const stykker = rest.split('\n\n');
            rest = stykker.pop();
            for (const stykke of stykker) {
              const linjer = stykke.split('\n');
              if (linjer[0].startsWith(':')) continue;          // hjerteslag
              const navn = linjer.find(l => l.startsWith('event:'));
              if (navn && navn.includes('borte')) { levende = false; setSelected(null); break; }
              const data = linjer.filter(l => l.startsWith('data:'))
                                 .map(l => l.slice(5).trim()).join('');
              if (!data || data === '{}') continue;
              try {
                const dok = JSON.parse(data);
                setDetail(dok);
                // Listen i venstre side viser status og skal foelge med. Den
                // baerer ikke underskriverne, saa de pilles fra — ellers ville
                // hvert listepunkt slaebe rundt paa en kopi af dem.
                const { signers: _, ...uden } = dok;
                setDocs(d => d.map(x => (x.id === dok.id ? { ...x, ...uden } : x)));
              } catch { /* et halvt stykke — det kommer igen */ }
            }
          }
        } catch (e) {
          if (afbryd.signal.aborted) return;
          // Netvaerket knak, eller stroemmen naaede sin levetid. Vent lidt og
          // prøv igen — uden pausen ville et fejlende endepunkt give en
          // uendelig loekke af forbindelser.
          await new Promise(r => setTimeout(r, 3000));
        }
      }
    };

    lyt();
    return () => { levende = false; afbryd.abort(); };
  }, [selected]);

  const sendDocument = async () => {
    if (!detail?.signers?.length) { toast.error('Tilføj mindst én underskriver først'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/documents/${selected}/send`);
      toast.success(r.data?.sent ? `Sendt til ${r.data.sent} underskriver(e)` : 'Sendt');
      fetchDetail(selected); fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke sende');
    } finally { setBusy(false); }
  };

  // Rykker praecis én underskriver. "Send igen" paa dokumentet rammer alle der
  // ikke har svaret — inklusive afsenderen selv, hvis han staar paa listen.
  const sendTilEn = async (signer) => {
    // Bekraeftelse foer afsendelse: knappen sidder taet paa "kopiér link" og
    // "fjern", og en mail kan ikke kaldes tilbage.
    if (!window.confirm(
      `Send underskriftslinket igen til ${signer.email}?\n\n` +
      'Modtageren får en ny mail og skal underskrive dokumentet.'
    )) return;
    setBusy(true);
    try {
      await axios.post(`${API}/documents/${selected}/signers/${signer.id}/send`);
      toast.success(`Sendt igen til ${signer.email}`);
      fetchDetail(selected); fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke sende');
    } finally { setBusy(false); }
  };

  const archiveDocument = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/documents/${selected}/archive`);
      toast.success('Dokument arkiveret');
      fetchDetail(selected); fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke arkivere');
    } finally { setBusy(false); }
  };

  const cancelDocument = async () => {
    if (!window.confirm('Annuller dokumentet? Underskrivere kan ikke længere underskrive.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/documents/${selected}/cancel`);
      toast.success('Dokument annulleret');
      fetchDetail(selected); fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke annullere');
    } finally { setBusy(false); }
  };

  const deleteDocument = async (id) => {
    if (!window.confirm('Slet dokumentet permanent?')) return;
    try {
      await axios.delete(`${API}/documents/${id}`);
      toast.success('Dokument slettet');
      if (selected === id) setSelected(null);
      fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke slette');
    }
  };

  const removeSigner = async (sid) => {
    if (!window.confirm('Fjern underskriveren?')) return;
    try {
      await axios.delete(`${API}/documents/${selected}/signers/${sid}`);
      toast.success('Underskriver fjernet');
      fetchDetail(selected);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke fjerne');
    }
  };

  // Filen og attesten ligger bag login, men et almindeligt <a href> sender ingen
  // Authorization-header — browseren fik derfor {"detail":"Not authenticated"}.
  // Derfor hentes indholdet med axios (som har headeren) og vises som blob-URL.
  const hentBeskyttetBlob = async (sti) => {
    const r = await axios.get(`${API}${sti}`, { responseType: 'blob' });
    return r.data;
  };

  const fejltekst = async (e, fallback) => {
    // Fejlsvaret er også en blob, når responseType er 'blob'.
    try {
      const tekst = await e.response?.data?.text?.();
      return JSON.parse(tekst)?.detail || fallback;
    } catch {
      return fallback;
    }
  };

  // Aabner i nyt faneblad. Vinduet aabnes FOER await, mens klikket stadig
  // taeller som en brugerhandling — ellers blokerer browseren pop-up'en.
  const visBlob = async (sti, fejlbesked) => {
    const vindue = window.open('', '_blank');
    setBusy(true);
    try {
      const blob = await hentBeskyttetBlob(sti);
      const url = URL.createObjectURL(blob);
      if (!vindue) {
        toast.error('Tillad pop-up-vinduer for at se filen');
        URL.revokeObjectURL(url);
        return;
      }
      vindue.location = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      vindue?.close();
      toast.error(await fejltekst(e, fejlbesked));
    } finally { setBusy(false); }
  };

  const downloadBlob = async (sti, filnavn, fejlbesked) => {
    setBusy(true);
    try {
      const blob = await hentBeskyttetBlob(sti);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filnavn || 'dokument';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(await fejltekst(e, fejlbesked));
    } finally { setBusy(false); }
  };

  // Det underskrevne eksemplar hedder noget andet end originalen, saa de to
  // ikke lander i samme mappe med samme navn og bliver forvekslet.
  const underskrevetNavn = (filnavn) =>
    (filnavn || 'dokument').replace(/\.pdf$/i, '') + '-underskrevet.pdf';

  const copyLink = (signer) => {
    const url = `${window.location.origin}/sign?token=${signer.id}&e=${signer.email_token || ''}`;
    navigator.clipboard?.writeText(url)
      .then(() => toast.success('Link kopieret'))
      .catch(() => toast.error('Kunne ikke kopiere'));
  };

  // ── Detaljevisning ──
  if (selected) {
    const d = detail;
    const signers = detail?.signers || [];
    const meta = STATUS_META[d?.status] || STATUS_META.draft;
    const canSend = d && ['draft', 'pending'].includes(d.status) && signers.length > 0;
    const canEdit = d && ['draft', 'pending'].includes(d.status);

    return (
      <div className="max-w-4xl mx-auto">
        <button onClick={() => setSelected(null)}
          className="text-sm text-th-text-secondary hover:text-th-text mb-4 inline-flex items-center gap-1.5 transition-colors">
          ← Tilbage til dokumenter
        </button>

        {detailLoading || !d ? (
          <Card className="p-12 bg-th-input border-th-border text-center">
            <p className="text-th-text-secondary">Indlæser...</p>
          </Card>
        ) : (
          <>
            <div className="mb-6 flex flex-col sm:flex-row sm:items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <h1 className="text-2xl sm:text-3xl font-heading font-bold text-th-text break-words">{d.title}</h1>
                  <Badge className={`${meta.color} text-white shrink-0`}>{meta.label}</Badge>
                </div>
                <p className="text-sm text-th-text-secondary">
                  {d.filename} · {fmtBytes(d.size)} · oprettet {fmtDate(d.created_at)}
                </p>
                {d.sha256 && (
                  <p className="text-[11px] text-th-text-faint font-mono mt-1 break-all">
                    SHA-256: {d.sha256}
                  </p>
                )}
              </div>
            </div>

            {/* Handlinger */}
            <div className="flex flex-wrap gap-2 mb-6">
              {canSend && (
                <button onClick={sendDocument} disabled={busy}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-btn hover:bg-btn-hover-strong text-btn-fg transition-colors disabled:opacity-50">
                  <Send size={15} /> {d.status === 'pending' ? 'Send igen' : 'Send til underskrift'}
                </button>
              )}
              {/* Er dokumentet faerdigt, er det stemplede eksemplar dét, ejeren
                  skal have fat i — originalen uden underskriftsside beviser
                  intet. Foer den tid findes stemplet ikke, og originalen er
                  den eneste fil, der er. */}
              {d.status === 'signed' ? (
                <>
                  <button onClick={() => visBlob(`/documents/${d.id}/signed-file`, 'Kunne ikke vise den underskrevne fil')} disabled={busy}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-th-border text-th-text-secondary hover:bg-th-hover transition-colors disabled:opacity-50">
                    <Eye size={15} /> Se underskrevet fil
                  </button>
                  <button onClick={() => downloadBlob(`/documents/${d.id}/signed-file`, underskrevetNavn(d.filename), 'Kunne ikke hente den underskrevne fil')} disabled={busy}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-th-border text-th-text-secondary hover:bg-th-hover transition-colors disabled:opacity-50">
                    <Download size={15} /> Hent
                  </button>
                  <button onClick={() => visBlob(`/documents/${d.id}/certificate`, 'Kunne ikke hente attesten')} disabled={busy}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-green-600 hover:bg-green-700 text-white transition-colors disabled:opacity-50">
                    <FileCheck size={15} /> Se attest
                  </button>
                </>
              ) : (
                <button onClick={() => downloadBlob(`/documents/${d.id}/file`, d.filename, 'Kunne ikke hente filen')} disabled={busy}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-th-border text-th-text-secondary hover:bg-th-hover transition-colors disabled:opacity-50">
                  <Download size={15} /> Hent fil
                </button>
              )}
              {d.status !== 'archived' && d.status !== 'cancelled' && (
                <button onClick={archiveDocument} disabled={busy}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-th-border text-th-text-secondary hover:bg-th-hover transition-colors disabled:opacity-50">
                  <Archive size={15} /> Arkivér
                </button>
              )}
              {d.status === 'pending' && (
                <button onClick={cancelDocument} disabled={busy}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-red-500/40 text-red-500 hover:bg-red-500/10 transition-colors disabled:opacity-50">
                  <Ban size={15} /> Annuller
                </button>
              )}
            </div>

            {/* Naeste skridt. Ved 0 underskrivere siger tomme-tilstanden det
                allerede, saa den vises kun naar der er noget nyt at fortaelle. */}
            {d.purpose === 'sign' && signers.length > 0
              && d.status !== 'cancelled' && d.status !== 'archived' && (
              <div className="mb-5 flex items-start gap-2.5 px-4 py-3 rounded-xl bg-brand-500/5 border border-brand-500/20">
                <AlertCircle size={16} className="text-brand-500 mt-0.5 shrink-0" />
                <p className="text-sm text-th-text-secondary">
                  {d.status === 'draft'
                    ? <>Næste skridt: klik <strong className="text-th-text">Send til underskrift</strong> for at sende links ud på e-mail.</>
                    : d.status === 'signed'
                      ? <>Alle har underskrevet. Den <strong className="text-th-text">underskrevne fil</strong> har dokumentnøgle i marginen og en underskriftsside til sidst. <strong className="text-th-text">Attesten</strong> rummer den fulde dokumentation for verifikationen.</>
                      : <>Sendt — afventer at underskriverne gennemlæser og godkender via e-mail og {d.kraev_mitid ? 'MitID' : 'SMS'}.</>}
                </p>
              </div>
            )}

            {/* Underskrivere */}
            {d.purpose === 'sign' ? (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-th-text">
                    Underskrivere <span className="text-th-text-muted font-normal">({signers.length})</span>
                  </h2>
                  {/* Kun her naar der allerede er en liste — ved 0 underskrivere
                      staar knappen i tomme-tilstanden nedenfor. */}
                  {canEdit && signers.length > 0 && (
                    <button onClick={() => setShowAddSigner(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-btn hover:bg-btn-hover-strong text-btn-fg transition-colors">
                      <Plus size={15} /> Tilføj
                    </button>
                  )}
                </div>

                {signers.length === 0 ? (
                  <Card className="p-8 bg-th-input border-th-border text-center">
                    <Users size={40} className="text-th-text-faint mx-auto mb-3" />
                    <p className="text-th-text font-medium">Tilføj dem der skal underskrive</p>
                    <p className="text-th-text-muted text-sm mt-1 mb-5 max-w-sm mx-auto">
                      Du skal angive navn, e-mail og mobilnummer på hver underskriver.
                      Når de er tilføjet, kan du sende dokumentet til underskrift.
                    </p>
                    {canEdit && (
                      <button onClick={() => setShowAddSigner(true)}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-btn hover:bg-btn-hover-strong text-btn-fg transition-colors">
                        <Plus size={16} /> Tilføj underskriver
                      </button>
                    )}
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {signers.map(s => {
                      const st = SIGNER_STATUS[s.status] || SIGNER_STATUS.pending;
                      return (
                        <Card key={s.id} className="p-4 bg-th-input border-th-border">
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-th-text font-medium">{s.name}</p>
                                <span className={`text-xs font-medium ${st.color}`}>{st.label}</span>
                              </div>
                              <p className="text-sm text-th-text-secondary truncate">{s.email}</p>
                              <p className="text-sm text-th-text-muted">{s.phone_masked || s.phone}</p>
                              {s.company_name && (
                                <p className="text-xs text-th-text-muted mt-1 flex items-center gap-1">
                                  <Building size={12} /> {s.company_name}
                                  {s.company_address ? ` · ${s.company_address}` : ''}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {canSend && !['signed', 'declined'].includes(s.status) && (
                                <button onClick={() => sendTilEn(s)} disabled={busy}
                                  title={`Send underskriftslinket igen til ${s.email}`}
                                  className="p-2 rounded-lg text-th-text-muted hover:text-brand-500 hover:bg-th-hover transition-colors disabled:opacity-50">
                                  <Send size={16} />
                                </button>
                              )}
                              {d.status === 'pending' && s.status !== 'signed' && (
                                <button onClick={() => copyLink(s)} title="Kopiér underskriftslink"
                                  className="p-2 rounded-lg text-th-text-muted hover:text-brand-500 hover:bg-th-hover transition-colors">
                                  <Eye size={16} />
                                </button>
                              )}
                              {canEdit && s.status !== 'signed' && (
                                <button onClick={() => removeSigner(s.id)} title="Fjern"
                                  className="p-2 rounded-lg text-th-text-muted hover:text-red-500 hover:bg-th-hover transition-colors">
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Attestationer */}
                          <div className="grid sm:grid-cols-2 gap-2">
                            <AttestRow
                              icon={MailCheck} label="E-mail godkendt"
                              at={s.email_verified_at} sessionId={s.email_session_id}
                              extra={s.email_ip ? `IP: ${s.email_ip}` : null}
                            />
                            {d.kraev_mitid ? (
                              <AttestRow
                                icon={ShieldCheck} label="MitID godkendt"
                                at={s.mitid_verified_at} sessionId={s.mitid_sub}
                                extra={s.mitid_name ? `Navn i MitID: ${s.mitid_name}` : null}
                              />
                            ) : (
                              <AttestRow
                                icon={MessageSquare} label="SMS godkendt"
                                at={s.sms_verified_at} sessionId={s.sms_session_id}
                                extra={s.sms_reply_text ? `Svar: "${s.sms_reply_text}"` : null}
                              />
                            )}
                          </div>

                          {(s.read_at || s.signed_at || s.declined_at) && (
                            <div className="mt-2 pt-2 border-t border-th-border-light space-y-0.5">
                              {s.read_at && (
                                <p className="text-xs text-th-text-muted">
                                  Gennemlæst {fmtDate(s.read_at)}
                                  {s.read_seconds ? ` (${s.read_seconds}s)` : ''}
                                </p>
                              )}
                              {s.signed_at && (
                                <p className="text-xs text-green-500 font-medium">
                                  Underskrevet {fmtDate(s.signed_at)}
                                </p>
                              )}
                              {s.declined_at && (
                                <p className="text-xs text-red-500">
                                  Afvist {fmtDate(s.declined_at)}
                                  {s.decline_reason ? ` — "${s.decline_reason}"` : ''}
                                </p>
                              )}
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <Card className="p-6 bg-th-input border-th-border">
                <div className="flex items-center gap-3">
                  <Archive size={22} className="text-brand-500 shrink-0" />
                  <div>
                    <p className="text-th-text font-medium">Arkiveringsdokument</p>
                    <p className="text-sm text-th-text-secondary">
                      Dette dokument er gemt til arkivering og kræver ingen underskrift.
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </>
        )}

        {showAddSigner && (
          <AddSignerDialog
            documentId={selected}
            onClose={() => setShowAddSigner(false)}
            onAdded={() => { fetchDetail(selected); fetchDocs(); }}
          />
        )}
      </div>
    );
  }

  // ── Opret-guide ──
  if (showUpload) {
    return (
      <DocumentWizard
        onClose={() => setShowUpload(false)}
        onDone={(docId) => { setShowUpload(false); fetchDocs(); if (docId) setSelected(docId); }}
      />
    );
  }

  // ── Listevisning ──
  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-th-text mb-1 sm:mb-2">Dokumenter</h1>
          <p className="text-sm sm:text-base text-th-text-secondary">
            Upload dokumenter til arkivering eller digital underskrift
          </p>
        </div>
        <div className="shrink-0 flex items-center gap-2">
          {/* Indstillingerne hoerer til dokumenterne — hvordan en
              underskriftsmail ser ud — og ikke til selskabet som helhed.
              Derfor her og ikke i hovedmenuen. */}
          <button onClick={() => navigate('/dokumenter/indstillinger')}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-th-border text-th-text-secondary hover:border-brand-400 hover:text-brand-500 transition-colors">
            <Settings size={15} /> Indstillinger
          </button>
          {/* DigitalBox: API'et er en del af dokumenterne — det aflever dokumenter
              til underskrift — og har derfor plads her i stedet for i menuen */}
          {BRAND.appDesign === 'tre-kolonner' && (
            <button onClick={() => navigate('/dokumenter/api')}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-th-border text-th-text-secondary hover:border-brand-400 hover:text-brand-500 transition-colors">
              <Key size={15} /> API
            </button>
          )}
          <button onClick={() => setShowUpload(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-btn hover:bg-btn-hover-strong text-btn-fg transition-colors">
            <Plus size={15} /> Nyt dokument
          </button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-20 rounded-xl bg-th-hover/40 animate-pulse" />
          ))}
        </div>
      ) : docs.length === 0 ? (
        <Card className="p-12 bg-th-input border-th-border text-center">
          <FileText size={48} className="text-th-text-faint mx-auto mb-4" />
          <p className="text-th-text-secondary text-lg font-medium">Ingen dokumenter endnu</p>
          <p className="text-th-text-muted text-sm mt-1">
            Upload et dokument for at arkivere det eller sende det til underskrift
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {docs.map(d => {
            const meta = STATUS_META[d.status] || STATUS_META.draft;
            const Icon = meta.icon;
            const total = d.signer_count || 0;
            const done = d.signed_count || 0;
            return (
              <Card key={d.id}
                className="p-4 bg-th-input border-th-border hover:border-th-border-hover transition-colors cursor-pointer"
                onClick={() => setSelected(d.id)}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="mt-0.5 shrink-0">
                      <Icon size={20} className="text-brand-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-th-text font-medium truncate">{d.title}</p>
                      <p className="text-sm text-th-text-muted truncate">
                        {d.filename} · {fmtBytes(d.size)}
                      </p>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="text-xs text-th-text-faint">{fmtDate(d.created_at)}</span>
                        {d.purpose === 'sign' && total > 0 && (
                          <span className={`text-xs font-medium ${done === total ? 'text-green-500' : 'text-amber-500'}`}>
                            {done}/{total} underskrevet
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={`${meta.color} text-white`}>{meta.label}</Badge>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteDocument(d.id); }}
                      title="Slet"
                      className="p-1.5 rounded-lg text-th-text-muted hover:text-red-500 hover:bg-th-hover transition-colors">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

    </div>
  );
}

export default DocumentsView;
